import { createTheme } from "@mui/material/styles";

// ── Generator Console — design tokens ───────────────────────────────────────
// An operator control surface for a GPU rack. Sky-blue #2E8BE6 is the signal
// color (established brand); IBM Plex Mono carries all data (VRAM, %, °C, W, IDs)
// so the whole dashboard reads like an instrument panel. The cyan→sky gradient is
// reserved for the one signature element: the VRAM headroom gauge.
export const tokens = {
  signal: "#2E8BE6",
  signalDeep: "#1F6FC0",
  ink: "#0E1B2E",
  inkRail: "#0d1a2b",
  slate: "#5A6B85",
  mist: "#F5F8FC",
  paper: "#FFFFFF",
  line: "#E4EAF2",
  ok: "#16A34A",
  warn: "#D97706",
  crit: "#DC2626",
  vramGradient: "linear-gradient(90deg, #2E8BE6 0%, #38BDF8 100%)",
};

export const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
export const SANS = '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// A reusable mono "eyebrow" label treatment (section headers, units, IDs).
export const eyebrow = {
  fontFamily: MONO,
  fontSize: ".68rem",
  fontWeight: 500,
  letterSpacing: ".14em",
  textTransform: "uppercase",
  color: tokens.slate,
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: tokens.signal, dark: tokens.signalDeep, contrastText: "#fff" },
    secondary: { main: tokens.ink },
    background: { default: tokens.mist, paper: tokens.paper },
    text: { primary: tokens.ink, secondary: tokens.slate },
    divider: tokens.line,
    success: { main: tokens.ok },
    warning: { main: tokens.warn },
    info: { main: tokens.signal },
    error: { main: tokens.crit },
  },
  typography: {
    fontFamily: SANS,
    fontWeightMedium: 600,
    h5: { fontWeight: 700, letterSpacing: "-0.01em", fontSize: "1.4rem" },
    h6: { fontWeight: 700, letterSpacing: "-0.01em", fontSize: "1.05rem" },
    button: { textTransform: "none", fontWeight: 600 },
    caption: { color: tokens.slate },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: tokens.mist },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": { background: "#cdd7e6", borderRadius: 8, border: "2px solid transparent", backgroundClip: "padding-box" },
        "*::-webkit-scrollbar-thumb:hover": { background: "#aebbd0", backgroundClip: "padding-box" },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: `1px solid ${tokens.line}`,
          borderRadius: 14,
          boxShadow: "0 1px 2px rgba(14,27,46,.04)",
          transition: "box-shadow .18s ease, border-color .18s ease, transform .18s ease",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 9, fontWeight: 600 },
        contained: { boxShadow: "0 1px 2px rgba(46,139,230,.25)" },
        outlined: { borderColor: tokens.line, color: tokens.ink, "&:hover": { borderColor: "#c7d3e6", background: "#f3f7fc" } },
        sizeSmall: { fontSize: ".78rem", paddingTop: 3, paddingBottom: 3 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 7 },
        sizeSmall: { height: 21, fontSize: ".7rem" },
        outlined: { borderColor: tokens.line },
        label: { paddingLeft: 8, paddingRight: 8 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: tokens.line, padding: "10px 12px" },
        head: {
          ...eyebrow,
          fontSize: ".64rem",
          color: tokens.slate,
          background: "#fafcfe",
          borderBottom: `1px solid ${tokens.line}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { "&:hover": { background: "#f7fafd" }, "&:last-child td": { borderBottom: 0 } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: "#fff",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.line },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#c7d3e6" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tokens.signal, borderWidth: 1.5 },
        },
        input: { "&::placeholder": { color: "#9aa8bf", opacity: 1 } },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { fontSize: ".9rem" } } },
    MuiSwitch: {
      styleOverrides: {
        root: { "& .Mui-checked": { color: tokens.signal }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.signal } },
      },
    },
    MuiLinearProgress: { styleOverrides: { root: { borderRadius: 6, backgroundColor: "#eaf0f8" } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 16, border: `1px solid ${tokens.line}` } } },
    MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: "1.05rem" } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { background: tokens.ink, fontSize: ".72rem", fontWeight: 500, borderRadius: 7, padding: "5px 9px" },
        arrow: { color: tokens.ink },
      },
    },
    MuiAccordion: {
      styleOverrides: { root: { "&:before": { display: "none" }, boxShadow: "none" } },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 10, alignItems: "center" } } },
  },
});

export default theme;
